import {
  faCalendar,
  faComment,
  faDownload,
  faEye,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React from 'react';
import {
  WORK_INSTRUCTION_REPORT_TYPE_ANSWERS,
  WORK_INSTRUCTION_TYPES,
} from 'src/utils/constants';

const formatReportContent = (instruction, report) => {
  const { report_type: reportType } = instruction;
  const rawValue = report.report;

  if (reportType === WORK_INSTRUCTION_TYPES.CHECKBOX) {
    try {
      const isChecked = JSON.parse(rawValue);
      return isChecked ? 'Checked' : 'Unchecked';
    } catch {
      return '';
    }
  }

  const predefined = WORK_INSTRUCTION_REPORT_TYPE_ANSWERS[reportType];

  if (predefined) return predefined;

  if (Array.isArray(rawValue)) {
    return rawValue.join(' - ');
  }

  return rawValue ?? '';
};

const WorkflowItem = ({
  instruction,
  report,
  documents,
  users,
  stepNumber,
  index,
  onPreview,
}) => {
  const file = documents.find(doc => doc.uri === report.report_file?.uri) ?? null;
  const src = file?.name ?? '';
  const canPreview = /(\.pdf|\.png|\.jpe?g|\.docx)(?:$|\?)/i.test(src);

  const hasReport = report.report !== undefined

  const content = hasReport
    ? formatReportContent(instruction, report)
    : '';

  return (
    <div className="collapsable-content__body-inner">
      <div style={{ fontSize: '14px' }}>
        <span className="font-bold">{stepNumber}.{index + 1}</span> {instruction.description}
      </div>
      <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
        {instruction.report_type && (
          <span className="status-badge">
            {instruction.report_type}
          </span>
        )}
        {instruction.threshold && (
          <span className="status-badge status-badge--notice">
            Threshold
          </span>
        )}
        {instruction.required && (
          <span className="status-badge status-badge--error">
            Required
          </span>
        )}
        {(!report || report.length === 0) &&
          instruction.report_type !== WORK_INSTRUCTION_TYPES.NO_ENTRY && (
            <span className="status-badge status-badge--warning">
              Pending
            </span>
          )}
        {hasReport && (
          <span className="status-badge status-badge--info">
            Complete
          </span>
        )}
      </div>
      {hasReport && (
        <div className="collapsable-content__body-highlight">
          <div className="collapsable-content__body-group font-bold">
            <FontAwesomeIcon icon={faComment} />
            <span style={{ flex: 1 }}>
              {content}
            </span>
            <div className="btn-group btn-group-sm ml-auto wi-doc-toolbar" role="group" aria-label="Document toolbar">
              {canPreview && file && (
                <button
                  type="button"
                  className="btn btn-outline-secondary d-inline-flex align-items-center wi-doc-btn wi-doc-btn-view"
                  title="View"
                  aria-label="View"
                  onClick={() => onPreview(file)}
                >
                  <FontAwesomeIcon icon={faEye} />
                </button>
              )}
              {file && (
                <a
                  className="btn btn-outline-secondary d-inline-flex align-items-center wi-doc-btn wi-doc-btn-download"
                  href={file.content}
                  download={file.name || 'document'}
                  title="Download"
                  aria-label="Download"
                >
                  <FontAwesomeIcon icon={faDownload} />
                </a>
              )}
            </div>
          </div>
          <div className="d-flex flex-wrap" style={{ rowGap: '8px', columnGap: '16px' }}>
            <div className="collapsable-content__body-group collapsable-content__body-group--secondary">
              <FontAwesomeIcon icon={faUser} />
              {users[report.user]?.name}
            </div>
            <div className="collapsable-content__body-group collapsable-content__body-group--secondary">
              <FontAwesomeIcon icon={faCalendar} />
              {report.completed != null ? dayjs(report.completed).format('MMM D, YYYY h:mm A') : ''}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

WorkflowItem.propTypes = {
  instruction: PropTypes.object,
  report: PropTypes.arrayOf(PropTypes.object),
  users: PropTypes.object,
  documents: PropTypes.arrayOf(PropTypes.shape({})),
  stepNumber: PropTypes.string,
  index: PropTypes.number,
  onPreview: () => { },
};

export default WorkflowItem
